import React, {useState} from 'react';
import {t} from 'i18next';
import {useFetchRecommendProblemsQuery} from '@goproblems/shared/api/indexPage';
import IndexSectionHeading from './IndexSectionHeading';
import ProblemPictureSkeleton from '@components/skeleton/ProblemPictureSkeleton';
import useScreenSize from '@utils/hooks/useScreenSize';
import {getNumberOfItemsPerRow} from '@utils/hooks/useNumberOfItemsPerRow';
import {ProblemMinimal} from '@types';
import RankText from '@components/common/RankText';
import {ThemeAwareSkeleton} from '@components/theme/ThemeAwareSkeleton';
import {ThemeAwareBadge} from '@components/theme/ThemeAwareBadge';
import {Link} from 'react-router';
import {useTranslation} from 'react-i18next';

const RecommendedProblemCard: React.FC<{
    problem: ProblemMinimal;
}> = ({problem}) => {
    const {t} = useTranslation();
    const [imageLoaded, setImageLoaded] = useState(false);

    return (
        <div className="card relative">
            <span className="absolute inset-x-0 top-0 z-10">
                <ThemeAwareBadge className="m-1">
                    <RankText rank={problem.rank} />
                </ThemeAwareBadge>
            </span>
            <Link
                to={`/problems/${problem.id}?name=recommended`}
                className="text-decoration-none">
                {!imageLoaded && (
                    <ThemeAwareSkeleton
                        style={{
                            width: '100%',
                            aspectRatio: 1.02, // compensate for the size gap between the skeleton and the image
                        }}
                    />
                )}
                <img
                    onLoad={() => setImageLoaded(true)}
                    className="block h-auto w-full"
                    style={{display: imageLoaded ? 'block' : 'none'}}
                    src={problem.imageUrl}
                    alt={t('common.board_thumbnail')}
                />
            </Link>
        </div>
    );
};

const getTwoColumnNumberOfItems = (width: number): number => {
    if (width >= 1400) return 5;
    if (width >= 1200) return 4;
    return 3;
};

const RecommendedProblem: React.FC<{fullWidth?: boolean}> = ({
    fullWidth = false,
}) => {
    const {width} = useScreenSize();
    const {
        data: recommendedProblems,
        isLoading,
        isError,
    } = useFetchRecommendProblemsQuery();
    const isTwoColumnLayout = width >= 992 && !fullWidth;
    const numberOfItems = isTwoColumnLayout
        ? getTwoColumnNumberOfItems(width)
        : getNumberOfItemsPerRow(width);

    const renderVisibilityClasses = (index: number): string => {
        if (isTwoColumnLayout) {
            if (index >= 5) return 'hidden';
            if (index >= 4) return 'hidden site-xxl:block';
            if (index >= 3) return 'hidden site-xl:block';
            if (index >= 2) return 'hidden md:block';
        } else {
            if (index >= 8) return 'hidden site-xxl:block';
            if (index >= 6) return 'hidden site-xl:block';
            if (index >= 4) return 'hidden md:block';
            if (index >= 2) return 'hidden site-sm:block';
        }
        return '';
    };

    const renderSkeletons = () =>
        Array.from({
            length: numberOfItems,
        }).map((_, index) => (
            <div
                key={`skeleton-${index}`}
                className={`min-w-0 flex-1 ${renderVisibilityClasses(index)}`}>
                <ProblemPictureSkeleton />
            </div>
        ));

    const renderProblems = () =>
        recommendedProblems
            ?.slice(0, numberOfItems)
            .map((problem, index) => (
                <div
                    key={problem.id}
                    className={`min-w-0 flex-1 ${renderVisibilityClasses(index)}`}>
                    <RecommendedProblemCard problem={problem} />
                </div>
            ));

    if (isError) {
        return (
            <div>{t('index.section_load_error')}</div>
        );
    }

    if (!isLoading && recommendedProblems?.length === 0) {
        return null;
    }

    return (
        <div className="index-home-feature index-home-card-list w-full">
            <IndexSectionHeading
                title={t('index.recommended_problems')}
                to="/problems/recommendations"
            />
            <div className="flex flex-nowrap gap-2">
                {isLoading ? renderSkeletons() : renderProblems()}
            </div>
        </div>
    );
};

export default RecommendedProblem;
